import React, { useState } from 'react';
import { navigate, type RouteComponentProps } from '@gatsbyjs/reach-router';

import JustificationContainer from '~/components/atom/JustificationContainer';
import { Body, Heading2, Heading4, Label } from '~/components/atom/Typography';
import Modal from '~/components/organism/ModalsV2/Modal';
import Overlay from '~/components/organism/ModalsV2/Overlay';
import TipBanner from '~/components/organism/TipBanner';
import ChatLink from '~/components/organism/TipBanner/ChatLink';
import Input from '~/components/molecule/Input';
import Button from '~/components/atom/Button';
import TextButton from '~/components/atom/TextButton';
import Catalog from '~/Catalog';
import isValidToken from './utils/isValidToken';
import Link from '~/components/molecule/Link';
import {
  useGetFlowDependenciesLazyQuery,
  useCopyAndInsertFlowMutation,
} from '~/graphql/types';
import useCurrentAccount from '~/hooks/useCurrentAccount';
import useAddToast from '~/hooks/useAddToast';
import formatToastMessage from '~/util/formatToastMessage';
import useGlobalKeyBinding from '~/hooks/useGlobalKeyBinding';
import EditableText from '~/components/organism/EditableText';
import Icon from '~/components/atom/Icon';
import useTooltipLayer from '~/hooks/useTooltipLayer';
import styled, { css } from 'styled-components';
import ResultWrapper from './components/ResultWrapper';
import { HELP_DESK_LINK } from '~/constants';
import useTrackEvent from '~/hooks/useTrackEvent';

export type Props = {
  onClose: () => void;
} & RouteComponentProps;

const text = {
  header: 'Importeer een gedeelde flow',
  tipHeader: 'Hulp nodig?',
  tipBody: (
    <>
      Kom je er niet uit? Lees de volledige instructie in{' '}
      <Link
        to={`${HELP_DESK_LINK}/nl/articles/10071733-een-automation-flow-delen`}
        target="_blank"
      >
        onze kennisbank
      </Link>{' '}
      of <ChatLink linkText="start een chat met ons." />
    </>
  ),
  description: (
    <>
      Importeer een flow. Gebruik de token van de flow dat met jou is gedeeld.
    </>
  ),
  tokenLabel: 'Plak token',
  flowDescriptionLabel: 'Omschrijving',
  flowNameLabel: 'Naam',

  submitLabel: 'Flow importeren',

  invalidToken: 'Deze token is niet geldig',
  importFlowError:
    'Er is iets misgegaan bij het importeren van de flow. Probeer het opnieuw.',

  importSuccess: 'Flow is succesvol toegevoegd.',
  importSuccessFlowAndForms:
    'Flow en formulieren zijn succesvol toegevoegd aan je account.',
  formDependencies: 'Formulieren die worden toegevoegd:',
  formDependenciesTooltip:
    'Deze formulieren worden toegevoegd aan je account. Dit is nodig om de flow te kunnen gebruiken.',
};

const ImportFlow: React.FCC<Props> = ({ dataTestId, onClose, ...rest }) => {
  const [importData, setImportData] = useState<{
    name: string;
    description: string;
    token: string;
  }>({
    name: '',
    description: '',
    token: '',
  });

  const [flowFound, setFlowFound] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [touched, setTouched] = useState<boolean>(false);

  const { id: accountId } = useCurrentAccount();
  const addToast = useAddToast();
  const trackEvent = useTrackEvent();
  const [
    getFlowDependencies,
    { data: flowDependencies, loading: loadingDependencies },
  ] = useGetFlowDependenciesLazyQuery({});

  const onTokenChange = async (value: string) => {
    if (!value || !isValidToken(value)) {
      setLoading(false);
      setFlowFound(false);
      return;
    }

    await getFlowDependencies({
      variables: {
        accountId,
        token: value,
      },
      fetchPolicy: 'network-only',
    })
      .then(({ data }) => {
        if (data?.getFlowDependencies) {
          const flowName = data?.getFlowDependencies.name;
          const flowDescription = data?.getFlowDependencies.description;

          setImportData(prev => ({
            ...prev,
            name: flowName ?? '',
            description: flowDescription ?? '',
          }));
          return setFlowFound(true);
        }

        setFlowFound(false);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const requiredForms = flowDependencies?.getFlowDependencies.requiredForms;
  const [copyAndInsertFlow, { loading: copyLoading }] =
    useCopyAndInsertFlowMutation({});

  const onSubmit = async () => {
    const flow = await copyAndInsertFlow({
      variables: {
        accountId,
        token: importData.token,
        opts: {
          name: importData.name,
          description: importData.description || null,
        },
      },
    });

    if (flow.data == null || flow.errors) {
      return addToast([formatToastMessage(text.importFlowError, 'danger')]);
    }

    /** ----- TRACKING: flow_imported ----- */
    trackEvent({
      eventName: 'flow_imported',
      parameters: {
        flow_id: flow.data.copyFlow.id,
      },
    });
    addToast([
      formatToastMessage(
        hasDependencies ? text.importSuccessFlowAndForms : text.importSuccess,
        'success',
      ),
    ]);
    void navigate(
      `/-/automation/flows/builder-v2/update/${flow.data.copyFlow.id}`,
    );
  };

  useGlobalKeyBinding({
    keys: 'enter',
    callback: onSubmit,
    enabled: isValidToken(importData.token),
  });

  const hasDependencies = requiredForms && requiredForms.length > 0;
  const isLoading = loading || loadingDependencies;
  const isTokenValid = isValidToken(importData.token);
  const tooltipProps = useTooltipLayer({
    tooltipMessage: text.formDependenciesTooltip,
  });

  return (
    <Overlay onClose={onClose}>
      <Modal maxWidth="800px">
        <JustificationContainer
          direction="column"
          padding={['xl']}
          width="100%"
          data-testid={dataTestId}
          {...rest}
        >
          <JustificationContainer>
            <Heading2>{text.header}</Heading2>
          </JustificationContainer>
          <Body>{text.description}</Body>
          <TipBanner
            id="share-form-tip"
            headerText={text.tipHeader}
            dismissible={false}
            width="100%"
          >
            <Body size="s">{text.tipBody}</Body>
          </TipBanner>
          <JustificationContainer
            direction="column"
            width="100%"
            gap="m"
            margin={[null, null, 'm', null]}
          >
            <Input
              width="100%"
              externalErrors={
                touched && !isTokenValid ? [text.invalidToken] : []
              }
              label={{ text: text.tokenLabel }}
              validation={[
                val => (isValidToken(val) ? true : text.invalidToken),
              ]}
              placeholder="xxxxxx-xxxx-xxx-xxx-xxxxxxx"
              onChange={event => {
                setTouched(true);
                setLoading(isValidToken(event.target.value));
                setImportData(prev => ({ ...prev, token: event.target.value }));
                void onTokenChange(event.target.value);
              }}
              value={importData.token}
              autoFocus
            />

            <ResultWrapper
              noFlowFound={!flowFound}
              isLoading={isLoading}
              noToken={importData.token.length === 0}
            >
              <JustificationContainer width="100%" gap="base" align="center">
                <Label size="base" margin={[null]}>
                  {text.flowNameLabel}
                </Label>
                <EditableText
                  text={importData.name}
                  onSave={value => {
                    setImportData(prev => ({
                      ...prev,
                      name: value,
                    }));
                  }}
                  fontWeight="regular"
                  size="base"
                  iconSize="base"
                />
              </JustificationContainer>
              <JustificationContainer width="100%" gap="base" align="center">
                <Label size="base" margin={[null]}>
                  {text.flowDescriptionLabel}
                </Label>
                <EditableText
                  text={importData.description}
                  onSave={value => {
                    setImportData(prev => ({
                      ...prev,
                      description: value,
                    }));
                  }}
                  fontWeight="regular"
                  size="base"
                  iconSize="base"
                  allowEmpty
                />
              </JustificationContainer>
              {hasDependencies && (
                <JustificationContainer width="100%" direction="column">
                  <Heading4 margin={[null]} size="base">
                    {text.formDependencies}{' '}
                    <Icon name="question-mark" {...tooltipProps} />
                  </Heading4>

                  <StyledList>
                    {requiredForms.map(form => (
                      <li key={form.id}>{form.name}</li>
                    ))}
                  </StyledList>
                </JustificationContainer>
              )}
            </ResultWrapper>
          </JustificationContainer>

          <JustificationContainer width="100%" justification="space-between">
            <TextButton label={Catalog.cancel} onClick={onClose} />
            <Button
              label={text.submitLabel}
              onClick={onSubmit}
              size="medium"
              disabled={!isValidToken(importData.token)}
              loading={copyLoading}
            />
          </JustificationContainer>
        </JustificationContainer>
      </Modal>
    </Overlay>
  );
};

const StyledList = styled.ul(
  ({ theme }) => css`
    list-style: none;
    list-style-type: disc;
    padding-left: ${theme.space('l')};
  `,
);

export default ImportFlow;
